import React, { useState, useEffect, useCallback } from 'react';
import { getPlayers, getInjuries, getIllnesses } from '../api';

// RTP removed (confirmed 2026-07-28) — RTC is now the final/closing stage.
const STAGE_LABELS = {
  medical_rehab: 'Medical Rehab',
  ofr:           'OFR',
  rtt:           'RTT',
  rtc:           'RTC',
};

const STAGE_COLORS = {
  medical_rehab: '#ef4444',
  ofr:           '#f59e0b',
  rtt:           '#8b5cf6',
  rtc:           '#10b981',
};

const ILLNESS_COLORS = {
  reported:   '#ef4444',
  recovering: '#f59e0b',
  cleared:    '#10b981',
};

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' });
}

function daysSince(d) {
  if (!d) return null;
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
}

function Avatar({ player, size = 64 }) {
  if (player?.photo_url) {
    return (
      <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', background: 'var(--bg-app)' }}>
        <img src={player.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} />
      </div>
    );
  }
  const initials = (player?.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg,#2563eb,#3b82f6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: size * 0.34, color: '#fff',
    }}>
      {initials}
    </div>
  );
}

function ConditionRow({ cond }) {
  if (cond.kind === 'injury') {
    const color = STAGE_COLORS[cond.current_stage] || '#ef4444';
    const days = daysSince(cond.updated_at || cond.created_at);
    const erd = fmtDate(cond.expected_return_date);
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 13, fontWeight: 800, padding: '3px 10px', borderRadius: 99,
            background: color + '22', color, border: `1px solid ${color}55`,
          }}>{STAGE_LABELS[cond.current_stage] || cond.current_stage}</span>
          <span style={{ fontSize: 15, color: 'var(--text-secondary)', fontWeight: 600 }}>{cond.body_part}</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
          {cond.injury_type}
          {days != null && ` · ${days}d in stage`}
          {erd && ` · ERD ${erd}`}
        </div>
      </div>
    );
  }
  const color = ILLNESS_COLORS[cond.status] || '#f59e0b';
  const erd = fmtDate(cond.expected_return_date);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 13, fontWeight: 800, padding: '3px 10px', borderRadius: 99,
          background: color + '22', color, border: `1px solid ${color}55`,
        }}>{cond.status}</span>
        <span style={{ fontSize: 15, color: 'var(--text-secondary)', fontWeight: 600 }}>{cond.illness_type}</span>
        {cond.contagious && <span style={{ fontSize: 12, fontWeight: 800, color: '#ef4444' }}>⚠ Contagious</span>}
      </div>
      {erd && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>ERD {erd}</div>}
    </div>
  );
}

function PlayerCard({ entry }) {
  const isInjury = entry.conditions.some(c => c.kind === 'injury');
  const borderColor = isInjury
    ? (STAGE_COLORS[entry.conditions.find(c => c.kind === 'injury').current_stage] || '#ef4444')
    : (ILLNESS_COLORS[entry.conditions.find(c => c.kind === 'illness').status] || '#f59e0b');

  return (
    <div style={{
      background: 'var(--bg-card)', border: `1px solid ${borderColor}55`, borderLeft: `4px solid ${borderColor}`,
      borderRadius: 14, padding: '18px 20px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Avatar player={entry.playerRecord} />
        <div>
          <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--text-primary)' }}>{entry.player_name}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {[entry.playerRecord?.position, entry.playerRecord?.squad_number ? `#${entry.playerRecord.squad_number}` : null].filter(Boolean).join(' · ') || ' '}
          </div>
        </div>
      </div>
      {entry.conditions.map((c, i) => <ConditionRow key={i} cond={c} />)}
    </div>
  );
}

export default function MedicalBoard() {
  const [players, setPlayers]     = useState([]);
  const [affected, setAffected]   = useState([]);
  const [now, setNow]             = useState(new Date());
  const [loading, setLoading]     = useState(true);

  const refresh = useCallback(() => {
    Promise.all([getPlayers(), getInjuries(), getIllnesses()]).then(([pl, inj, ill]) => {
      const byPlayer = {};
      inj.filter(i => !i.is_closed).forEach(i => {
        byPlayer[i.player_id] = byPlayer[i.player_id] || { player_id: i.player_id, player_name: i.player_name, conditions: [] };
        byPlayer[i.player_id].conditions.push({ kind: 'injury', ...i });
      });
      ill.filter(i => i.status !== 'cleared').forEach(i => {
        byPlayer[i.player_id] = byPlayer[i.player_id] || { player_id: i.player_id, player_name: i.player_name, conditions: [] };
        byPlayer[i.player_id].conditions.push({ kind: 'illness', ...i });
      });
      const list = Object.values(byPlayer).map(e => ({
        ...e,
        playerRecord: pl.find(p => String(p.id) === String(e.player_id)) || null,
      })).sort((a, b) => a.player_name.localeCompare(b.player_name));
      setPlayers(pl);
      setAffected(list);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
    const dataTimer  = setInterval(refresh, 30000);
    const clockTimer = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(dataTimer); clearInterval(clockTimer); };
  }, [refresh]);

  const totalSquad     = players.filter(p => p.active !== false).length;
  const unavailable     = affected.length;
  const available       = Math.max(0, totalSquad - unavailable);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-app)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif', fontSize: 22 }}>
        Loading medical board…
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif', padding: '32px 40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28 }}>
        <div>
          <div style={{ fontSize: 30, fontWeight: 900, fontStyle: 'italic', letterSpacing: '-0.02em' }}>
            HeraLab<span style={{ color: '#2563eb' }}>X</span> <span style={{ fontWeight: 400, fontStyle: 'normal', fontSize: 22, color: 'var(--text-muted)' }}>Medical Status</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 28, fontWeight: 700, fontFamily: 'monospace' }}>
            {now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
            {now.toLocaleDateString('nl-NL', { weekday: 'long', day: '2-digit', month: 'long' })}
          </div>
        </div>
      </div>

      {/* Summary strip */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 32 }}>
        {[
          { label: 'Squad', value: totalSquad, color: '#3b82f6' },
          { label: 'Available', value: available, color: '#10b981' },
          { label: 'Unavailable', value: unavailable, color: unavailable > 0 ? '#ef4444' : '#10b981' },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 14, padding: '16px 24px' }}>
            <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 38, fontWeight: 900, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Player grid */}
      {affected.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '40vh', gap: 12 }}>
          <div style={{ fontSize: 56 }}>✅</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#10b981' }}>Full squad available</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 18 }}>
          {affected.map(entry => <PlayerCard key={entry.player_id} entry={entry} />)}
        </div>
      )}

      <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-dimmed)', marginTop: 32 }}>
        Auto-refreshing every 30s
      </div>
    </div>
  );
}
